import { DateInput } from "../components/DateInput";
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useSettings, formatMoney } from "../lib/settings";
import { Download, FileText } from "lucide-react";

import { tr, lbl } from "../lib/i18n";
const RANGES = [
  { k: "today", l: tr("x.today") }, { k: "week", l: tr("x.this_week") }, { k: "month", l: tr("x.this_month") },
  { k: "quarter", l: tr("x.this_quarter") }, { k: "year", l: tr("x.this_year") }, { k: "custom", l: tr("x.custom") },
];

function useReport(name, range, from, to) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!name || (range === "custom" && (!from || !to))) return;
    api.get(`/reports/${name}`, { params: { range, date_from: from || undefined, date_to: to || undefined } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [name, range, from, to]);
  return data;
}

export default function ReportsPage() {
  const { settings } = useSettings();
  const [reports, setReports] = useState([]);
  const [name, setName] = useState("");
  const [range, setRange] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const data = useReport(name, range, from, to);

  useEffect(() => {
    api.get("/reports").then(({ data }) => { setReports(data.items); setName(data.items[0]?.key || ""); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const download = async (kind) => {
    try {
      const params = { range, date_from: from || undefined, date_to: to || undefined };
      const { data: blob } = await api.get(`/reports/${name}/${kind}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${name}_${range}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const money = new Set(data?.money_columns || []);
  const show = (v, h) => (money.has(h) && typeof v === "number" ? formatMoney(v, settings) : (v ?? "—"));

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kicker">{tr("x.analytics")}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>{tr("x.reports")}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-excel" onClick={() => download("excel")} className="btn-secondary" disabled={!data}><Download size={14} /> Excel</button>
          <button data-testid="export-pdf" onClick={() => download("pdf")} className="btn-primary" disabled={!data}><FileText size={14} /> PDF</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="report-list">
        {reports.map((r) => (
          <button key={r.key} onClick={() => setName(r.key)} data-testid={`report-${r.key}`}
            className={name === r.key ? "btn-primary" : "btn-secondary"} style={{ minHeight: 36 }}>{r.label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="seg">
          {RANGES.map((r) => (
            <button key={r.k} onClick={() => setRange(r.k)} className={range === r.k ? "active" : ""} data-testid={`range-${r.k}`}>{r.l}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <DateInput type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="range-from" />
            <span className="text-xs opacity-60 rtl-flip inline-block">→</span>
            <DateInput type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="range-to" />
          </div>
        )}
      </div>

      {data && (
        <div className="panel overflow-x-auto" data-testid="report-table">
          <div className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{data.title} · {data.start} → {data.end} · {data.rows.length} {tr("x.rows")}</div>
          <table className="w-full dense-table">
            <thead><tr>{data.headers.map((h, i) => <th key={h}>{data.labels?.[i] || h}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i}>{row.map((v, j) => <td key={j} className={money.has(data.headers[j]) ? "tabular" : ""}>{lbl(show(v, data.headers[j]))}</td>)}</tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={data.headers.length} className="text-center py-8 opacity-60">{tr("x.no_data")}</td></tr>}
            </tbody>
            {data.rows.length > 0 && money.size > 0 && (
              <tfoot>
                <tr data-testid="report-totals" style={{ fontWeight: 600, borderTop: "2px solid var(--text)" }}>
                  {data.totals.map((v, j) => <td key={j} className="tabular">{v == null ? "" : lbl(show(v, data.headers[j]))}</td>)}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
